'use client';
import Link from 'next/link';
import { useSystemHealth } from '@/hooks/use-system-health';
import { cn } from '@/lib/cn';
import { Urls } from '@/lib/urls';
import { MenuProvider } from '@/providers/menu-provider';
import { ActivityIcon, ChevronLeftIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Logo } from '../ui/logo';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import SettingsNavigation from './components/SettingsNavigation';
import { DashboardNavigation } from './components/dashboard-navigation';
import { FloatingMenu } from './components/floating-menu';
import SystemStatus from './components/floating-menu/components/system-status';
import { OrganizationSelector } from './components/organization-selector';
import { GlobalFilterSelector } from '@/lib/global-filter/components/global-filter-selector';
import { GlobalSearch } from '@/lib/global-search';

export function Sidebar({
  variant,
}: {
  variant: 'dashboard' | 'settings' | 'documents';
}) {
  const [systemStatusOpen, setSystemStatusOpen] = useState(false);

  const { data: systemHealth } = useSystemHealth();

  const renderMainSidebar = variant === 'dashboard' || variant === 'documents';

  const renderSettingsSidebar = variant === 'settings';

  return (
    <>
      {systemStatusOpen && (
        <SystemStatus closeFunction={() => setSystemStatusOpen(false)} />
      )}

      <MenuProvider>
        <nav className='relative flex h-screen max-h-window-keeper-height w-full max-w-[300px] min-w-[270px] flex-col border-r bg-surface p-0 shadow-[inset_0px_0px_10px_rgba(0,0,0,0.05)]'>
          {variant === 'settings' && (
            <div className='border-b p-4 py-[19px]'>
              <div className='flex items-center justify-between'>
                <Link
                  prefetch={'auto'}
                  href='/dashboard'
                  className='flex items-center bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden'
                >
                  <ChevronLeftIcon className='mr-2 size-5' />
                  <h2>Kontrollcenter</h2>
                </Link>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => setSystemStatusOpen(true)}
                      variant={'outline'}
                      size={'icon'}
                      className={cn('relative', {
                        'animate-pulse bg-yellow-200':
                          !!systemHealth?.maintenance?.eta,
                        'animate-pulse bg-destructive-subtle':
                          systemHealth?.maintenance?.active,
                      })}
                    >
                      <ActivityIcon className='size-4 text-foreground' />
                      <div
                        className={cn(
                          'absolute -top-1 -right-1 size-3 rounded-full border border-background',
                          {
                            'bg-success':
                              !systemHealth?.maintenance?.active &&
                              !systemHealth?.maintenance?.eta,
                            'bg-yellow-600': !!systemHealth?.maintenance?.eta,
                            'bg-destructive': systemHealth?.maintenance?.active,
                          }
                        )}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Systemstatus</TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
          {(variant === 'dashboard' || variant === 'documents') && (
            <div className='flex flex-col items-center gap-3 p-4 pb-2'>
              <div className='flex w-full items-start gap-4'>
                <Link
                  prefetch={'auto'}
                  href={Urls.dashboard.home}
                  className='shrink-0'
                >
                  <Logo />
                </Link>
                <OrganizationSelector />
              </div>
              <div className='flex w-full flex-row items-center justify-between'>
                <GlobalFilterSelector />
              </div>
            </div>
          )}
          <div className='px-4 pb-2'>
            <GlobalSearch hiddenInput={variant === 'settings'} />
          </div>

          <div className='relative min-w-full'>
            <div className='absolute inset-x-0 z-10 min-h-[12px] bg-linear-to-b from-surface to-transparent dark:from-surface'></div>
          </div>
          <div className='scrollbar-hide overflow-y-scroll pb-28'>
            {renderMainSidebar && <DashboardNavigation />}
            {renderSettingsSidebar && <SettingsNavigation />}
          </div>
          <FloatingMenu isSettings={variant === 'settings'} />
        </nav>
      </MenuProvider>
    </>
  );
}
